import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { CheckCircle, Celebration, EmojiEvents } from '@mui/icons-material';

const CompletionCelebration = () => {
  // Create confetti particles - more particles for better effect
  const confettiCount = 100;
  const confettiColors = ['#2B9FFF', '#667eea', '#764ba2', '#00CDBC', '#FFD700'];

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {/* Confetti - burst outward from center in all directions */}
      {Array.from({ length: confettiCount }, (_, i) => {
        // Create radial burst pattern - each particle gets a random angle
        const angle = (i / confettiCount) * 360 + Math.random() * 30;
        const radians = (angle * Math.PI) / 180;
        // Random distance from center - spread across entire screen and beyond
        const distance = 400 + Math.random() * 600;
        const centerX = window.innerWidth / 2;
        const centerY = window.innerHeight / 2;
        // Calculate end position based on angle and distance
        const endX = centerX + Math.cos(radians) * distance;
        const endY = centerY + Math.sin(radians) * distance;

        return (
          <motion.div
            key={i}
            initial={{
              x: centerX,
              y: centerY,
              opacity: 1,
              scale: 0,
            }}
            animate={{
              x: endX,
              y: endY,
              opacity: [1, 1, 0.8, 0],
              scale: [0, 1.2, 1, 0.6],
              rotate: Math.random() * 1080 - 540,
            }}
            transition={{
              duration: 2.5 + Math.random() * 1.5,
              ease: 'easeOut',
            }}
            style={{
              position: 'absolute',
              width: 8 + Math.random() * 10 + 'px',
              height: 8 + Math.random() * 10 + 'px',
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
              background: confettiColors[Math.floor(Math.random() * confettiColors.length)],
            }}
          />
        );
      })}

      {/* Success message */}
      <motion.div
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 15, delay: 0.2 }}
      >
        <Box
          sx={{
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
            borderRadius: '23px',
            padding: '48px',
            textAlign: 'center',
            boxShadow: '0px 30px 80px rgba(0, 0, 0, 0.3)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
            maxWidth: '500px',
          }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.4, duration: 0.6, type: 'spring', stiffness: 200, damping: 15 }}
          >
            <Box
              sx={{
                width: '100px',
                height: '100px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                boxShadow: '0 10px 40px rgba(43, 159, 255, 0.4)',
                position: 'relative',
              }}
            >
              <CheckCircle sx={{ fontSize: '60px', color: '#fff' }} />

              {/* Celebration sparkle icons */}
              <motion.div
                animate={{
                  rotate: 360,
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  type: 'tween',
                  ease: 'easeInOut',
                }}
                style={{
                  position: 'absolute',
                  top: -10,
                  right: -10,
                }}
              >
                <Celebration sx={{ fontSize: '28px', color: '#FFD700' }} />
              </motion.div>

              <motion.div
                animate={{
                  rotate: -360,
                  scale: [1, 1.3, 1],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  type: 'tween',
                  ease: 'easeInOut',
                }}
                style={{
                  position: 'absolute',
                  bottom: -10,
                  left: -10,
                }}
              >
                <EmojiEvents sx={{ fontSize: '28px', color: '#FFD700' }} />
              </motion.div>
            </Box>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontSize: { xs: '28px', sm: '34px' },
              }}
            >
              All Set!
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                fontSize: '16px',
                lineHeight: 1.6,
              }}
            >
              You're ready to start using NoteGo. Let's create something amazing together!
            </Typography>
          </motion.div>

          {/* Loading dots animation */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.3 }}>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 1 }}>
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: [1, 1.5, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1,
                    repeat: Infinity,
                    delay: i * 0.2,
                    ease: 'easeInOut',
                  }}
                  style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2B9FFF 0%, #667eea 100%)',
                  }}
                />
              ))}
            </Box>
          </motion.div>
        </Box>
      </motion.div>
    </Box>
  );
};

export default CompletionCelebration;
